import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Share, Plus, Home, MoreVertical, Download, Smartphone, Bell, Megaphone, Zap } from "lucide-react";

const SEEN_KEY = "ssc_install_prompt_seen";

const detectPlatform = () => {
    const ua = navigator.userAgent || "";
    const isIOS = /iphone|ipad|ipod/i.test(ua) || (/Macintosh/.test(ua) && navigator.maxTouchPoints > 1);
    if (isIOS) return "ios";
    if (/android/i.test(ua)) return "android";
    return "ios"; // default tab for desktop preview
};

const isStandalone = () =>
    window.matchMedia?.("(display-mode: standalone)").matches || window.navigator.standalone === true;

const STEPS = {
    ios: [
        { icon: Share, title: "Tap the Share button", body: "In Safari, tap the Share icon (a square with an up arrow) in the toolbar." },
        { icon: Plus, title: "Add to Home Screen", body: "Scroll down the share menu and tap \u201CAdd to Home Screen.\u201D" },
        { icon: Home, title: "Tap \u201CAdd\u201D", body: "Confirm and the Single Social Club icon lands on your home screen." },
    ],
    android: [
        { icon: MoreVertical, title: "Open the menu", body: "In Chrome, tap the three-dot menu (\u22EE) in the top-right corner." },
        { icon: Download, title: "Install app", body: "Tap \u201CInstall app\u201D or \u201CAdd to Home screen.\u201D" },
        { icon: Home, title: "Confirm", body: "Tap \u201CInstall\u201D and the app appears on your home screen." },
    ],
};

const BENEFITS = [
    { icon: Zap, label: "Best performance" },
    { icon: Bell, label: "Push notifications" },
    { icon: Megaphone, label: "Special announcements" },
];

export default function InstallAppPrompt() {
    const [open, setOpen] = useState(false);
    const [tab, setTab] = useState("ios");

    useEffect(() => {
        if (isStandalone() || localStorage.getItem(SEEN_KEY)) return;
        setTab(detectPlatform());
        let fired = false;
        const onScroll = () => {
            if (fired) return;
            if (window.scrollY > 320) {
                fired = true;
                setOpen(true);
                window.removeEventListener("scroll", onScroll);
            }
        };
        window.addEventListener("scroll", onScroll, { passive: true });
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    const dismiss = () => {
        localStorage.setItem(SEEN_KEY, "1");
        setOpen(false);
    };

    return (
        <AnimatePresence>
            {open && (
                <motion.div
                    className="fixed inset-0 z-[120] flex items-end justify-center sm:items-center"
                    initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                    data-testid="install-app-prompt"
                >
                    <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={dismiss} />
                    <motion.div
                        className="relative w-full max-w-md overflow-hidden rounded-t-3xl bg-background text-foreground shadow-2xl sm:rounded-3xl"
                        initial={{ y: 60, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: 60, opacity: 0 }}
                        transition={{ type: "spring", damping: 26, stiffness: 260 }}
                    >
                        <button
                            onClick={dismiss}
                            className="absolute right-4 top-4 z-10 grid h-8 w-8 place-items-center rounded-full bg-black/5 text-muted-foreground transition-colors hover:bg-black/10"
                            data-testid="install-prompt-close" aria-label="Close"
                        >
                            <X className="h-4 w-4" />
                        </button>

                        <div className="bg-gradient-to-br from-accent to-accent/85 px-6 pb-5 pt-7 text-accent-foreground">
                            <div className="flex items-center gap-3">
                                <div className="grid h-12 w-12 shrink-0 place-items-center rounded-2xl bg-white/15 ring-1 ring-white/25">
                                    <Smartphone className="h-6 w-6" />
                                </div>
                                <div>
                                    <h2 className="font-display text-lg font-700 leading-tight">Add us to your home screen</h2>
                                    <p className="text-sm text-accent-foreground/80">Takes 10 seconds — no app store needed.</p>
                                </div>
                            </div>
                            <div className="mt-4 flex flex-wrap gap-2">
                                {BENEFITS.map((b) => (
                                    <span key={b.label} className="inline-flex items-center gap-1.5 rounded-full bg-white/15 px-3 py-1 text-xs font-600">
                                        <b.icon className="h-3.5 w-3.5" /> {b.label}
                                    </span>
                                ))}
                            </div>
                        </div>

                        <div className="px-6 pb-7 pt-5">
                            <div className="mb-4 grid grid-cols-2 gap-1 rounded-full bg-muted p-1" data-testid="install-platform-tabs">
                                {["ios", "android"].map((p) => (
                                    <button
                                        key={p}
                                        onClick={() => setTab(p)}
                                        data-testid={`install-tab-${p}`}
                                        className={`rounded-full py-2 text-sm font-600 transition-colors ${tab === p ? "bg-background text-accent shadow-sm" : "text-muted-foreground"}`}
                                    >
                                        {p === "ios" ? "iPhone / iPad" : "Android"}
                                    </button>
                                ))}
                            </div>

                            <ol className="space-y-3">
                                {STEPS[tab].map((s, i) => (
                                    <li key={i} className="flex items-start gap-3" data-testid={`install-step-${tab}-${i + 1}`}>
                                        <span className="grid h-8 w-8 shrink-0 place-items-center rounded-full bg-accent/15 text-sm font-700 text-accent">
                                            {i + 1}
                                        </span>
                                        <div className="min-w-0">
                                            <p className="flex items-center gap-1.5 text-sm font-600 text-primary">
                                                <s.icon className="h-4 w-4 text-accent" /> {s.title}
                                            </p>
                                            <p className="text-sm text-muted-foreground">{s.body}</p>
                                        </div>
                                    </li>
                                ))}
                            </ol>

                            <button
                                onClick={dismiss}
                                className="mt-6 w-full rounded-full bg-accent py-3 text-sm font-700 text-accent-foreground transition-opacity hover:opacity-90"
                                data-testid="install-prompt-got-it"
                            >
                                Got it
                            </button>
                        </div>
                    </motion.div>
                </motion.div>
            )}
        </AnimatePresence>
    );
}
